import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, MessageSquare, Trash2, Send } from "lucide-react";
import { LEAD_STATUSES, statusByValue } from "@/lib/constants";
import { groupTagsByCategory } from "@/lib/tagUtils";
import { useConfirm } from "@/hooks/useConfirm";

export default function ContactProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [noteText, setNoteText] = useState("");

  const load = async () => {
    const { data } = await api.get(`/contacts/${id}`);
    setData(data);
    setForm(data.contact);
  };

  useEffect(() => {
    load();
    api.get("/tags").then(({ data }) => setAllTags(data));
    // eslint-disable-next-line
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { name, phone, email, city, country, lead_source, notes, status, tag_ids } = form;
      await api.patch(`/contacts/${id}`, { name, phone, email, city, country, lead_source, notes, status, tag_ids });
      toast.success("Saved");
      load();
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId) => {
    const tag_ids = form.tag_ids || [];
    const next = tag_ids.includes(tagId) ? tag_ids.filter((t) => t !== tagId) : [...tag_ids, tagId];
    setForm({ ...form, tag_ids: next });
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    await api.post("/notes", { contact_id: id, body: noteText });
    setNoteText("");
    load();
    toast.success("Note added");
  };

  const deleteContact = async () => {
    const ok = await confirm({
      title: "Delete this contact?",
      description: "All notes, timeline events and conversation history will remain but the contact record will be removed.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Deleted");
    navigate("/contacts");
  };

  const openInbox = async () => {
    const { data } = await api.post("/conversations", { contact_id: id });
    navigate(`/inbox?conversation=${data.id}`);
  };

  if (!data) return <div className="p-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const status = statusByValue(form.status);

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="contact-profile-page">
      <Link to="/contacts" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-contacts">
        <ArrowLeft className="w-4 h-4 mr-1" />Back to contacts
      </Link>

      <PageHeader
        title={form.name || "Contact"}
        description={form.phone}
        actions={
          <>
            <Button variant="outline" onClick={openInbox} data-testid="contact-open-inbox">
              <MessageSquare className="w-4 h-4 mr-1.5" />Open chat
            </Button>
            <Button variant="outline" onClick={deleteContact} className="text-red-600" data-testid="contact-delete">
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-dark" data-testid="contact-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />Save</>}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <div className="label-caps mb-4">Contact info</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: "name", label: "Name" },
                  { key: "phone", label: "Phone" },
                  { key: "email", label: "Email" },
                  { key: "city", label: "City" },
                  { key: "country", label: "Country" },
                  { key: "lead_source", label: "Lead source" },
                ].map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs text-slate-600">{f.label}</Label>
                    <Input
                      value={form[f.key] || ""}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      className="mt-1"
                      data-testid={`contact-field-${f.key}`}
                    />
                  </div>
                ))}
                <div>
                  <Label className="text-xs text-slate-600">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="mt-1" data-testid="contact-field-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-xs text-slate-600">Notes</Label>
                <textarea
                  value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  data-testid="contact-field-notes"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <Tabs defaultValue="timeline">
                <TabsList>
                  <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                  <TabsTrigger value="notes" data-testid="tab-notes">Notes ({data.notes.length})</TabsTrigger>
                </TabsList>
                <TabsContent value="timeline" className="mt-4">
                  {data.timeline.length === 0 ? (
                    <div className="text-sm text-slate-400 py-4">No events yet</div>
                  ) : (
                    <ul className="space-y-2 max-h-96 overflow-y-auto">
                      {data.timeline.map((e) => (
                        <li key={e.id} className="flex items-start gap-3 p-2 border-l-2 border-slate-200 pl-3">
                          <div className="flex-1">
                            <div className="text-sm text-slate-800">{e.title}</div>
                            <div className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString()}</div>
                          </div>
                          <Badge variant="secondary" className="text-[10px]">{e.event_type}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </TabsContent>
                <TabsContent value="notes" className="mt-4">
                  <div className="flex gap-2 mb-3">
                    <Input
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note…"
                      data-testid="contact-note-input"
                    />
                    <Button onClick={addNote} className="bg-brand hover:bg-brand-dark" data-testid="contact-note-add">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  <ul className="space-y-2 max-h-72 overflow-y-auto">
                    {data.notes.map((n) => (
                      <li key={n.id} className="p-3 rounded-md bg-amber-50 border border-amber-100">
                        <div className="text-sm text-slate-800 whitespace-pre-wrap">{n.body}</div>
                        <div className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                      </li>
                    ))}
                  </ul>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <div className="label-caps mb-3">Status</div>
              <span className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-medium ${status.color}`}>
                {status.label}
              </span>
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-6">
              <div className="label-caps mb-3">Tags</div>
              {(() => {
                const selected = new Set(form.tag_ids || []);
                const grouped = groupTagsByCategory(allTags, { excludeInactive: true });
                // Always show currently-assigned tags even if disabled, so the user
                // can see/un-assign them.
                const assignedInactive = allTags.filter((t) => selected.has(t.id) && t.is_active === false);
                if (grouped.length === 0 && assignedInactive.length === 0) {
                  return <div className="text-xs text-slate-400">No tags yet</div>;
                }
                return (
                  <div className="space-y-3">
                    {grouped.map(({ category, tags }) => (
                      <div key={category} data-testid={`contact-tag-group-${category}`}>
                        <div className="text-[10px] font-semibold tracking-wider uppercase text-slate-400 mb-1.5">{category}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {tags.map((t) => (
                            <button
                              key={t.id}
                              onClick={() => toggleTag(t.id)}
                              data-testid={`tag-toggle-${t.id}`}
                              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors ${
                                selected.has(t.id)
                                  ? "text-white border-transparent"
                                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                              }`}
                              style={selected.has(t.id) ? { backgroundColor: t.color } : undefined}
                            >
                              {t.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                    {assignedInactive.length > 0 && (
                      <div data-testid="contact-tag-group-inactive">
                        <div className="text-[10px] font-semibold tracking-wider uppercase text-slate-400 mb-1.5">Inactive (assigned)</div>
                        <div className="flex flex-wrap gap-1.5">
                          {assignedInactive.map((t) => (
                            <button
                              key={t.id}
                              onClick={() => toggleTag(t.id)}
                              data-testid={`tag-toggle-${t.id}`}
                              className="inline-flex items-center rounded-full border border-dashed px-2.5 py-0.5 text-xs font-medium text-white opacity-70"
                              style={{ backgroundColor: t.color }}
                            >
                              {t.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {data.conversation && (
            <Card className="border-slate-200 shadow-none">
              <CardContent className="p-6">
                <div className="label-caps mb-2">Conversation</div>
                <div className="text-sm text-slate-700">{data.conversation.last_message || "No messages yet"}</div>
                <div className="text-xs text-slate-400 mt-1">
                  {data.conversation.last_message_at ? new Date(data.conversation.last_message_at).toLocaleString() : "—"}
                </div>
                <Button variant="outline" size="sm" className="mt-3 w-full" onClick={openInbox}>
                  Open in inbox
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
